import { Deferred, Effect, Exit, Layer, Option, Schema } from 'effect';

import {
  PPL_WIDGET_ASSET_MARKER,
  PPL_WIDGET_CLOSE_EVENT,
  PPL_WIDGET_ERROR_EVENT,
  PPL_WIDGET_EVENT,
  PPL_WIDGET_LOADER_URL,
  PPL_WIDGET_READY_EVENT,
  PPL_WIDGET_TAG,
  PplSelectionSchema,
  PplWidget,
  PplWidgetAssetLoadError,
  PplWidgetCancelledError,
  PplWidgetNotLoadedError,
  PplWidgetPayloadError,
  PplWidgetRuntimeError,
  selectionCountry,
  toPickupPoint,
} from './PplWidget';
import type { PplWidgetError, PplWidgetOptions } from './PplWidget';
import type { PickupPoint } from '../shipping/PickupPoint';
import { createScript, loadOf } from '../shipping/domAsset';

const decodeSelection = Schema.decodeUnknownExit(PplSelectionSchema);
const loaderPromises = new WeakMap<Element, Promise<void>>();
const PPL_MAP_COLOR = '#004b93';

const loadLoader = (url: string) =>
  Effect.tryPromise({
    try: async () => {
      const existing = document.querySelector(`script[${PPL_WIDGET_ASSET_MARKER}]`);
      if (existing !== null) {
        if (existing.getAttribute(PPL_WIDGET_ASSET_MARKER) === 'loaded') {
          return;
        }
        if (existing.getAttribute(PPL_WIDGET_ASSET_MARKER) === 'pending') {
          return loaderPromises.get(existing) ?? loadOf(existing, 'the PPL widget loader');
        }
        existing.remove();
      }

      const script = createScript(url, { [PPL_WIDGET_ASSET_MARKER]: 'pending' });
      const loaded = loadOf(script, 'the PPL widget loader').then(
        () => {
          script.setAttribute(PPL_WIDGET_ASSET_MARKER, 'loaded');
        },
        (error: unknown) => {
          script.setAttribute(PPL_WIDGET_ASSET_MARKER, 'failed');
          throw error;
        }
      );
      loaderPromises.set(script, loaded);
      document.head.appendChild(script);
      return loaded;
    },
    catch: (cause) =>
      new PplWidgetAssetLoadError({ message: 'Failed to load the PPL widget loader', cause }),
  });

const locateEmbed = (selector: string) =>
  Effect.suspend(() => {
    const embed = typeof document === 'undefined' ? null : document.querySelector(selector);
    return embed === null
      ? Effect.fail(new PplWidgetNotLoadedError({ message: `No element matches "${selector}"` }))
      : Effect.succeed(embed);
  });

const eventDetail = (event: Event): unknown =>
  event instanceof CustomEvent ? event.detail : undefined;

type Coordinates = { lat: number; lng: number };

const locationFor = Effect.callback<Option.Option<Coordinates>>((resume) => {
  if (navigator.geolocation === undefined) {
    resume(Effect.succeed(Option.none()));
    return;
  }

  try {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resume(
          Effect.succeed(
            Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)
              ? Option.some({ lat: coords.latitude, lng: coords.longitude })
              : Option.none()
          )
        ),
      () => resume(Effect.succeed(Option.none())),
      { maximumAge: 300_000, timeout: 30_000 }
    );
  } catch {
    resume(Effect.succeed(Option.none()));
  }
});

type PplAccessPointElement = HTMLElement & {
  configure: (options: { centeredToLat: number; centeredToLon: number }) => void;
};

const isPplAccessPointElement = (element: HTMLElement): element is PplAccessPointElement =>
  'configure' in element && typeof element.configure === 'function';

const mountAndWait = (embed: Element, options: PplWidgetOptions) =>
  Effect.scoped(
    Effect.gen(function* () {
      const selection = yield* Deferred.make<PickupPoint, PplWidgetError>();
      const element = document.createElement(PPL_WIDGET_TAG);
      const hasCoordinates =
        options.lat !== undefined &&
        options.lng !== undefined &&
        Number.isFinite(options.lat) &&
        Number.isFinite(options.lng);
      let active = true;
      let ready = false;
      let settled = false;
      let browserCoordinates: Coordinates | undefined;
      const centerOnBrowser = () => {
        if (
          !active ||
          !ready ||
          settled ||
          browserCoordinates === undefined ||
          !isPplAccessPointElement(element)
        )
          return;
        element.configure({
          centeredToLat: browserCoordinates.lat,
          centeredToLon: browserCoordinates.lng,
        });
        browserCoordinates = undefined;
      };
      const onReady = () => {
        ready = true;
        centerOnBrowser();
      };
      element.setAttribute('api-key', options.apiKey);
      element.setAttribute(
        'config',
        JSON.stringify({
          viewMode: 'inline',
          defaultLang: options.language,
          defaultCountry: options.country,
          allowedCountries: [options.country],
          countriesMenuDisabled: true,
          ...(!hasCoordinates ? {} : { centeredToLat: options.lat, centeredToLon: options.lng }),
        })
      );

      const onSelect = (event: Event) => {
        settled = true;
        Deferred.doneUnsafe(
          selection,
          Exit.match(decodeSelection(eventDetail(event)), {
            onFailure: (cause) =>
              Effect.fail(
                new PplWidgetPayloadError({
                  message: 'The PPL widget sent an incomplete access point',
                  cause,
                })
              ),
            onSuccess: (point) => {
              const country = selectionCountry(point);
              return country === undefined || country.toUpperCase() === options.country
                ? toPickupPoint(point)
                : Effect.fail(
                    new PplWidgetPayloadError({
                      message: `PPL selection has country "${country}". Checkout requires "${options.country}".`,
                    })
                  );
            },
          })
        );
      };
      const onClose = () => {
        settled = true;
        Deferred.doneUnsafe(
          selection,
          Effect.fail(new PplWidgetCancelledError({ message: 'The shopper closed the PPL widget' }))
        );
      };
      const onAbort = () => {
        settled = true;
        Deferred.doneUnsafe(
          selection,
          Effect.fail(
            new PplWidgetCancelledError({ message: 'The checkout closed the PPL picker' })
          )
        );
      };
      const onError = (event: Event) => {
        settled = true;
        Deferred.doneUnsafe(
          selection,
          Effect.fail(
            new PplWidgetRuntimeError({
              message: 'The PPL widget reported an error',
              cause: eventDetail(event),
            })
          )
        );
      };

      yield* Effect.acquireRelease(
        Effect.sync(() => {
          // PPL reads this variable from the page root for cluster circles.
          const rootStyle = document.documentElement.style;
          const previousColor = rootStyle.getPropertyValue('--color-primary');
          const previousPriority = rootStyle.getPropertyPriority('--color-primary');
          rootStyle.setProperty('--color-primary', PPL_MAP_COLOR);
          element.addEventListener(PPL_WIDGET_EVENT, onSelect);
          element.addEventListener(PPL_WIDGET_CLOSE_EVENT, onClose);
          element.addEventListener(PPL_WIDGET_ERROR_EVENT, onError);
          element.addEventListener(PPL_WIDGET_READY_EVENT, onReady);
          options.signal?.addEventListener('abort', onAbort, { once: true });
          embed.appendChild(element);
          if (options.signal?.aborted === true) {
            onAbort();
          }
          return { previousColor, previousPriority };
        }),
        ({ previousColor, previousPriority }) =>
          Effect.sync(() => {
            active = false;
            element.removeEventListener(PPL_WIDGET_EVENT, onSelect);
            element.removeEventListener(PPL_WIDGET_CLOSE_EVENT, onClose);
            element.removeEventListener(PPL_WIDGET_ERROR_EVENT, onError);
            element.removeEventListener(PPL_WIDGET_READY_EVENT, onReady);
            options.signal?.removeEventListener('abort', onAbort);
            element.remove();
            if (previousColor === '') {
              document.documentElement.style.removeProperty('--color-primary');
            } else {
              document.documentElement.style.setProperty(
                '--color-primary',
                previousColor,
                previousPriority
              );
            }
          })
      );

      if (!hasCoordinates && !settled) {
        yield* Effect.forkScoped(
          Effect.map(locationFor, (coordinates) => {
            if (Option.isSome(coordinates)) {
              browserCoordinates = coordinates.value;
              centerOnBrowser();
            }
          })
        );
      }

      return yield* Deferred.await(selection);
    })
  );

const make = Effect.succeed({
  pick: (options: PplWidgetOptions) =>
    Effect.gen(function* () {
      const embed = yield* locateEmbed(options.embedSelector);
      yield* loadLoader(options.loaderUrl ?? PPL_WIDGET_LOADER_URL);
      return yield* mountAndWait(embed, options);
    }),
});

export const PplWidgetLive = Layer.effect(PplWidget, make);
